import { isBotDifficulty, type BotDifficulty } from "../../../bot/difficulty";
import {
  applyMove,
  applyVersion3RecordedMove,
  moveNotation,
  parseGame,
  parseLegacyGame,
  parseMoveNotation,
  parseVersion2Game,
  parseVersion3Game,
  positionKey,
  serializeGame,
  serializeLegacyGame,
  serializeVersion2Game,
  serializeVersion3Game,
  stateNotation,
  isThreefoldRepetition,
  type GameState,
  type Move,
  type Player,
  type SavedGameV1,
  type Transition,
} from "../../../game";
import type { MatchMode } from "../matchMode";
import { applyLegacyRecordedMove, applyVersion2RecordedMove } from "../../../game/transition";

export const MATCH_RECORDING_SCHEMA_VERSION = 1 as const;
export const LEGACY_MATCH_RECORD_SCHEMA_VERSION = 1 as const;
export const MATCH_RECORD_SCHEMA_VERSION = 2 as const;
export const MATCH_RULES_VERSION = 4 as const;
export const CLEANUP_PASSAGE_MATCH_RULES_VERSION = 3 as const;
export const PRE_REPETITION_MATCH_RULES_VERSION = 2 as const;
export const LEGACY_MATCH_RULES_VERSION = 1 as const;
export type MatchRulesVersion =
  | typeof LEGACY_MATCH_RULES_VERSION
  | typeof PRE_REPETITION_MATCH_RULES_VERSION
  | typeof CLEANUP_PASSAGE_MATCH_RULES_VERSION
  | typeof MATCH_RULES_VERSION;
export const MAX_HISTORY_ID_LENGTH = 128;

export type ManualDrawResult = Readonly<{ kind: "manual-draw" }>;
export type RepetitionDrawResult = Readonly<{ kind: "repetition-draw" }>;
export type AbandonedResult = Readonly<{ kind: "abandoned" }>;
export type MatchRecordResult =
  | Readonly<{ kind: "passage"; winner: Player }>
  | Readonly<{ kind: "no-moves"; winner: Player; loser: Player }>
  | ManualDrawResult
  | RepetitionDrawResult
  | AbandonedResult;

export type MatchRecordingV1 = Readonly<{
  schemaVersion: typeof MATCH_RECORDING_SCHEMA_VERSION;
  id: string;
  rulesVersion: MatchRulesVersion;
  initialGame: SavedGameV1;
  moves: readonly string[];
  completedAt: number | null;
}>;

type MatchRecordCommon = Readonly<{
  schemaVersion: typeof MATCH_RECORD_SCHEMA_VERSION;
  id: string;
  rulesVersion: MatchRulesVersion;
  completedAt: number;
  initialGame: SavedGameV1;
  moves: readonly string[];
  result: MatchRecordResult;
}>;

export type MatchRecordV1 =
  | (MatchRecordCommon &
      Readonly<{
        mode: "bot";
        humanPlayer: Player;
        difficulty: BotDifficulty;
        difficultyPolicyVersion: 1;
      }>)
  | (MatchRecordCommon & Readonly<{ mode: "local" }>);

export type MatchReplay = Readonly<{
  states: readonly GameState[];
  transitions: readonly Transition[];
  moves: readonly Move[];
  positionKeys: readonly string[];
}>;

export type MatchRecordParseResult =
  | Readonly<{ ok: true; record: MatchRecordV1; replay: MatchReplay }>
  | Readonly<{ ok: false; message: string }>;

export function usesRepetitionRules(rulesVersion: MatchRulesVersion): boolean {
  return rulesVersion >= CLEANUP_PASSAGE_MATCH_RULES_VERSION;
}

export function createMatchRecording(id: string, initialGame: GameState): MatchRecordingV1 {
  if (!isHistoryId(id)) throw new TypeError("Match history id is invalid.");
  return Object.freeze({
    schemaVersion: MATCH_RECORDING_SCHEMA_VERSION,
    id,
    rulesVersion: MATCH_RULES_VERSION,
    initialGame: serializeGame(initialGame),
    moves: Object.freeze([]),
    completedAt: null,
  });
}

export function appendRecordedMove(
  recording: MatchRecordingV1 | null,
  move: Parameters<typeof moveNotation>[0],
): MatchRecordingV1 | null {
  if (recording === null) return null;
  return Object.freeze({
    ...recording,
    moves: Object.freeze([...recording.moves, moveNotation(move)]),
  });
}

export function completeRecording(
  recording: MatchRecordingV1 | null,
  completedAt: number,
): MatchRecordingV1 | null {
  if (recording === null) return null;
  if (!isTimestamp(completedAt)) throw new TypeError("Match completion time is invalid.");
  return Object.freeze({ ...recording, completedAt });
}

export function parseMatchRecording(input: unknown): MatchRecordingV1 | null {
  if (
    !isRecord(input) ||
    !hasExactlyKeys(input, [
      "schemaVersion",
      "id",
      "rulesVersion",
      "initialGame",
      "moves",
      "completedAt",
    ])
  )
    return null;
  if (
    input["schemaVersion"] !== MATCH_RECORDING_SCHEMA_VERSION ||
    (input["rulesVersion"] !== LEGACY_MATCH_RULES_VERSION &&
      input["rulesVersion"] !== PRE_REPETITION_MATCH_RULES_VERSION &&
      input["rulesVersion"] !== CLEANUP_PASSAGE_MATCH_RULES_VERSION &&
      input["rulesVersion"] !== MATCH_RULES_VERSION) ||
    !isHistoryId(input["id"]) ||
    !Array.isArray(input["moves"]) ||
    !input["moves"].every((move) => typeof move === "string") ||
    (input["completedAt"] !== null && !isTimestamp(input["completedAt"]))
  )
    return null;
  const rulesVersion = input["rulesVersion"];
  const parsedInitial = parseGameForRulesVersion(input["initialGame"], rulesVersion);
  if (!parsedInitial.ok) return null;
  return Object.freeze({
    schemaVersion: MATCH_RECORDING_SCHEMA_VERSION,
    id: input["id"],
    rulesVersion,
    initialGame: serializeGameForRulesVersion(parsedInitial.state, rulesVersion),
    moves: Object.freeze([...input["moves"]]),
    completedAt: input["completedAt"],
  });
}

export function replayRecording(recording: MatchRecordingV1): MatchReplay | null {
  const initial = parseGameForRulesVersion(recording.initialGame, recording.rulesVersion);
  if (!initial.ok) return null;
  const states: GameState[] = [initial.state];
  const transitions: Transition[] = [];
  const moves = [];
  const positionKeys = [positionKey(initial.state)];
  let repetitionEnded = false;
  let state = initial.state;
  for (const notation of recording.moves) {
    if (usesRepetitionRules(recording.rulesVersion) && repetitionEnded) return null;
    const parsed = parseMoveNotation(notation);
    if (!parsed.ok) return null;
    const transition = (() => {
      if (recording.rulesVersion === LEGACY_MATCH_RULES_VERSION) {
        return applyLegacyRecordedMove(state, parsed.move);
      }
      if (recording.rulesVersion === PRE_REPETITION_MATCH_RULES_VERSION) {
        return applyVersion2RecordedMove(state, parsed.move);
      }
      if (recording.rulesVersion === CLEANUP_PASSAGE_MATCH_RULES_VERSION) {
        return applyVersion3RecordedMove(state, parsed.move);
      }
      const applied = applyMove(state, parsed.move);
      return applied.ok ? applied.transition : null;
    })();
    if (transition === null) return null;
    moves.push(parsed.move);
    transitions.push(transition);
    state = transition.state;
    states.push(state);
    if (state.outcome === null) {
      positionKeys.push(positionKey(state));
      repetitionEnded = isThreefoldRepetition(positionKeys, state);
    }
  }
  return Object.freeze({
    states: Object.freeze(states),
    transitions: Object.freeze(transitions),
    moves: Object.freeze(moves),
    positionKeys: Object.freeze(positionKeys),
  });
}

function parseGameForRulesVersion(input: unknown, rulesVersion: MatchRulesVersion) {
  if (rulesVersion === LEGACY_MATCH_RULES_VERSION) return parseLegacyGame(input);
  if (rulesVersion === PRE_REPETITION_MATCH_RULES_VERSION) return parseVersion2Game(input);
  if (rulesVersion === CLEANUP_PASSAGE_MATCH_RULES_VERSION) return parseVersion3Game(input);
  return parseGame(input);
}

function serializeGameForRulesVersion(game: GameState, rulesVersion: MatchRulesVersion) {
  if (rulesVersion === LEGACY_MATCH_RULES_VERSION) return serializeLegacyGame(game);
  if (rulesVersion === PRE_REPETITION_MATCH_RULES_VERSION) return serializeVersion2Game(game);
  if (rulesVersion === CLEANUP_PASSAGE_MATCH_RULES_VERSION) return serializeVersion3Game(game);
  return serializeGame(game);
}

export function recordingMatchesGame(recording: MatchRecordingV1, game: GameState): boolean {
  const replay = replayRecording(recording);
  if (replay === null) return false;
  const final = replay.states.at(-1);
  return final !== undefined && stateNotation(final) === stateNotation(game);
}

export function recordingEndsInThreefoldRepetition(recording: MatchRecordingV1): boolean {
  if (!usesRepetitionRules(recording.rulesVersion)) return false;
  const replay = replayRecording(recording);
  if (replay === null) return false;
  const final = replay.states.at(-1);
  if (final === undefined) return false;
  return final.outcome === null && isThreefoldRepetition(replay.positionKeys, final);
}

export function createMatchRecord(
  input: Readonly<{
    recording: MatchRecordingV1;
    mode: MatchMode;
    humanPlayer: Player;
    difficulty: BotDifficulty;
    difficultyPolicyVersion: 1;
    result: MatchRecordResult;
  }>,
): MatchRecordV1 {
  if (input.recording.completedAt === null) throw new TypeError("Completed record needs a time.");
  const common = {
    schemaVersion: MATCH_RECORD_SCHEMA_VERSION,
    id: input.recording.id,
    rulesVersion: input.recording.rulesVersion,
    completedAt: input.recording.completedAt,
    initialGame: input.recording.initialGame,
    moves: input.recording.moves,
    result: Object.freeze({ ...input.result }),
  } as const;
  return input.mode === "local"
    ? Object.freeze({ ...common, mode: "local" })
    : Object.freeze({
        ...common,
        mode: "bot",
        humanPlayer: input.humanPlayer,
        difficulty: input.difficulty,
        difficultyPolicyVersion: input.difficultyPolicyVersion,
      });
}

export function parseMatchRecord(input: unknown): MatchRecordParseResult {
  if (!isRecord(input)) return invalidRecord("Match record must be an object.");
  if (
    input["schemaVersion"] !== LEGACY_MATCH_RECORD_SCHEMA_VERSION &&
    input["schemaVersion"] !== MATCH_RECORD_SCHEMA_VERSION
  ) {
    return invalidRecord("Match record version is not supported.");
  }
  const mode = input["mode"];
  const expected =
    mode === "local"
      ? [
          "schemaVersion",
          "id",
          "rulesVersion",
          "completedAt",
          "initialGame",
          "moves",
          "result",
          "mode",
        ]
      : [
          "schemaVersion",
          "id",
          "rulesVersion",
          "completedAt",
          "initialGame",
          "moves",
          "result",
          "mode",
          "humanPlayer",
          "difficulty",
          "difficultyPolicyVersion",
        ];
  if (!hasExactlyKeys(input, expected)) return invalidRecord("Match record fields are malformed.");
  const recording = parseMatchRecording({
    schemaVersion: MATCH_RECORDING_SCHEMA_VERSION,
    id: input["id"],
    rulesVersion: input["rulesVersion"],
    initialGame: input["initialGame"],
    moves: input["moves"],
    completedAt: input["completedAt"],
  });
  if (recording?.completedAt === undefined || recording.completedAt === null)
    return invalidRecord("Match recording is invalid.");
  const result = parseRecordResult(input["result"]);
  if (result === null) return invalidRecord("Match result is invalid.");
  if (
    input["schemaVersion"] === LEGACY_MATCH_RECORD_SCHEMA_VERSION &&
    result.kind === "abandoned"
  ) {
    return invalidRecord("Legacy match records cannot contain abandonment.");
  }
  if (mode !== "local" && mode !== "bot") return invalidRecord("Match mode is invalid.");
  if (
    mode === "bot" &&
    ((input["humanPlayer"] !== "black" && input["humanPlayer"] !== "red") ||
      !isBotDifficulty(input["difficulty"]) ||
      input["difficultyPolicyVersion"] !== 1)
  ) {
    return invalidRecord("Bot match metadata is invalid.");
  }
  const record = createMatchRecord({
    recording,
    mode,
    humanPlayer: mode === "bot" ? (input["humanPlayer"] as Player) : "black",
    difficulty: mode === "bot" ? (input["difficulty"] as BotDifficulty) : "medium",
    difficultyPolicyVersion: 1,
    result,
  });
  const replay = replayRecording(recording);
  if (replay === null) return invalidRecord("Match contains an illegal move line.");
  const final = replay.states.at(-1);
  if (final === undefined) return invalidRecord("Match replay has no initial state.");
  if (!resultMatchesReplay(result, recording.rulesVersion, replay, final))
    return invalidRecord("Match result does not match its move line.");
  return Object.freeze({ ok: true, record, replay });
}

export function canonicalRecordJson(record: MatchRecordV1): string {
  return JSON.stringify(record);
}

function resultMatchesReplay(
  result: MatchRecordResult,
  rulesVersion: MatchRulesVersion,
  replay: MatchReplay,
  state: GameState,
): boolean {
  if (result.kind === "repetition-draw") {
    return (
      usesRepetitionRules(rulesVersion) &&
      state.outcome === null &&
      isThreefoldRepetition(replay.positionKeys, state)
    );
  }
  if (result.kind === "manual-draw" || result.kind === "abandoned") {
    return (
      state.outcome === null &&
      !(usesRepetitionRules(rulesVersion) && isThreefoldRepetition(replay.positionKeys, state))
    );
  }
  if (state.outcome?.kind !== result.kind || state.outcome.winner !== result.winner) return false;
  return (
    result.kind !== "no-moves" ||
    (state.outcome.kind === "no-moves" && state.outcome.loser === result.loser)
  );
}

function parseRecordResult(input: unknown): MatchRecordResult | null {
  if (!isRecord(input)) return null;
  if (input["kind"] === "abandoned" && hasExactlyKeys(input, ["kind"]))
    return Object.freeze({ kind: "abandoned" });
  if (input["kind"] === "manual-draw" && hasExactlyKeys(input, ["kind"]))
    return Object.freeze({ kind: "manual-draw" });
  if (input["kind"] === "repetition-draw" && hasExactlyKeys(input, ["kind"]))
    return Object.freeze({ kind: "repetition-draw" });
  if (
    input["kind"] === "passage" &&
    hasExactlyKeys(input, ["kind", "winner"]) &&
    isPlayer(input["winner"])
  ) {
    return Object.freeze({ kind: "passage", winner: input["winner"] });
  }
  if (
    input["kind"] === "no-moves" &&
    hasExactlyKeys(input, ["kind", "winner", "loser"]) &&
    isPlayer(input["winner"]) &&
    isPlayer(input["loser"])
  ) {
    return Object.freeze({ kind: "no-moves", winner: input["winner"], loser: input["loser"] });
  }
  return null;
}

function isPlayer(value: unknown): value is Player {
  return value === "black" || value === "red";
}
function isTimestamp(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}
function isHistoryId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_HISTORY_ID_LENGTH &&
    /^[A-Za-z0-9._:-]+$/u.test(value)
  );
}
function invalidRecord(message: string): MatchRecordParseResult {
  return Object.freeze({ ok: false, message });
}
function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function hasExactlyKeys(
  record: Readonly<Record<string, unknown>>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(record).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}
